import { action, computed, makeAutoObservable, runInAction } from "mobx"
import {
    CookingTime,
    ExternalIntegrationType,
    Menu,
    MenuCategory,
    MenuCategoryWorkingSchedule,
    MenuCategoryWorkingScheduleDay,
    MenuItem,
    MenuItemData,
    MenuItemModifier,
    MenuItemWorkingSchedule,
    MenuItemWorkingScheduleDay,
} from "../../../../services/menu/models";
import {
    createCategory,
    createMenuItem,
    deleteCategory,
    deleteMenuItem,
    getMenuById,
    updateCategory,
    updateMenuItem,
    updateMenuItemExternalId,
} from "../../../../services/menu";
import { locale } from "../../../../common/consts";
import { LocalizedString} from "../../../../services/general/models";
import MenuFormsStore, { MenuFormType } from "./forms";
import { ExecutionStatus } from "../../../../services/general/requestsResponses";
import { getStats } from "../../../../services/stats";
import { Moment } from "moment";


const defaultMenuCategoryWorkingScheduleDay: MenuCategoryWorkingScheduleDay = {
    isEnabled: false,
    slots: [
        {
            fromHour: 0,
            fromMinute: 0,
            toHour: 23,
            toMinute: 59,
        }
    ]
}

const defaultMenuCategoryWorkingSchedule: MenuCategoryWorkingSchedule = {
    isEnabled: false,
    monday: defaultMenuCategoryWorkingScheduleDay,
    tuesday: defaultMenuCategoryWorkingScheduleDay,
    wednesday: defaultMenuCategoryWorkingScheduleDay,
    thursday: defaultMenuCategoryWorkingScheduleDay,
    friday: defaultMenuCategoryWorkingScheduleDay,
    saturday: defaultMenuCategoryWorkingScheduleDay,
    sunday: defaultMenuCategoryWorkingScheduleDay,
}
const defaultMenuItemWorkingScheduleDay: MenuItemWorkingScheduleDay = {
    isEnabled: false,
    slots: [
        {
            fromHour: 0,
            fromMinute: 0,
            toHour: 23,
            toMinute: 59,
        }
    ]
}

const defaultMenuItemWorkingSchedule: MenuItemWorkingSchedule = {
    isEnabled: false,
    monday: defaultMenuItemWorkingScheduleDay,
    tuesday: defaultMenuItemWorkingScheduleDay,
    wednesday: defaultMenuItemWorkingScheduleDay,
    thursday: defaultMenuItemWorkingScheduleDay,
    friday: defaultMenuItemWorkingScheduleDay,
    saturday: defaultMenuItemWorkingScheduleDay,
    sunday: defaultMenuItemWorkingScheduleDay,
}


class MenuStore {
    forms = new MenuFormsStore();
    currentMenu?: {
        id: number,
        menuCategories: MenuCategory[];
        menuItemsByCategoryId: { [s: string]: MenuItem[] };
        productsById: { [key: number]: string },
        menu: Menu
    };
    currentMenuItem: MenuItem;
    selectedCategory: number = 0;
    modifier: MenuItemModifier;
    categoryWorkingSchedule: MenuCategoryWorkingSchedule = defaultMenuCategoryWorkingSchedule;
    itemWorkingSchedule: MenuItemWorkingSchedule = defaultMenuItemWorkingSchedule;
    categoryCookingTime: CookingTime = {fromTime: null, toTime: null};
    itemCookingTime: CookingTime = {fromTime: null, toTime: null};
    measurement: string | null = null;
    categoriesList: MenuCategory[] = [];
    newCategoryId: number | undefined = undefined;

    constructor() {
        makeAutoObservable(this);
    }


    async loadDetailsStats(from: Moment, to: Moment, id: number) {
        return await getStats({
            from: from.toDate(),
            to: to.toDate(),
            menuId: id,
        });
    }


    async getCategoriesByMenuId(menuId: number) {
        const response = await getMenuById({
            menuId: menuId
        });
        runInAction(() => {
            this.categoriesList = response.menuCategories;
        })
    }

    async fetchMenuById(menuId: number) {
        const response = await getMenuById({
            menuId: menuId
        });

        runInAction(() => {
            this.currentMenu = {
                id: menuId,
                menuCategories: response.menuCategories.sort((item1, item2) => {
                    return item1.data.sortOrder - item2.data.sortOrder
                }),
                menuItemsByCategoryId: response.menuItemsByCategoryId,
                productsById: response.productsById,
                menu: response.menu
            }
        })
    }

    async reloadMenu() {
        if (!this.currentMenu) return;
        await this.fetchMenuById(this.currentMenu.id)
    }



    async createCategory(params: {
        name: string,
        sortOrder: number,
        workingSchedule: MenuCategoryWorkingSchedule,
        parentMenuCategoryId: number | null,
        isInStopList: boolean,
        categoryCookingTime: CookingTime,
    }) {
        const res =await createCategory({
            data: {
                name: {
                    [locale]: params.name
                },
                sortOrder: params.sortOrder,
                workingSchedule: params.workingSchedule,
                parentMenuCategoryId: params.parentMenuCategoryId,
                isInStopList: params.isInStopList,
                categoryCookingTime: params.categoryCookingTime,
            },
            menuId: this.currentMenu.id
        });
        runInAction(() => {
            this.newCategoryId = res.menuCategoryId;
        })
        await this.reloadMenu();
    }

    async updateCategory(id: number, params: {
        name: LocalizedString,
        sortOrder: number,
        workingSchedule: MenuCategoryWorkingSchedule,
        parentMenuCategoryId: number | null,
        isInStopList: boolean,
        categoryCookingTime: CookingTime,
    }, forceReload: boolean = true) {
        await updateCategory({
            data: {
                name: params.name,
                sortOrder: params.sortOrder,
                workingSchedule: params.workingSchedule,
                parentMenuCategoryId: params.parentMenuCategoryId,
                isInStopList: params.isInStopList,
                categoryCookingTime: params.categoryCookingTime,
            },
            menuId: this.currentMenu.id,
            menuCategoryId: id
        });
        if (forceReload) await this.reloadMenu();
    }


    async removeCategory(menuId: number, menuCategoryId: number) {
        await deleteCategory({menuId, menuCategoryId});
        await this.reloadMenu();
    }

    async createMenuItem(params: MenuItemData) {
        const res = await createMenuItem({
            data: params,
            menuId: this.currentMenu.id,
        });
        await this.reloadMenu();
        return {
            err: res.executionStatus !== ExecutionStatus.Finished,
            menuItemId: res.menuItemId
        }
    }

    async updateMenuItem(id: number, params: MenuItemData, forceReload: boolean = true) {
        const res = await updateMenuItem({
            data: params,
            menuId: this.currentMenu.id,
            menuItemId: id
        });
        if (forceReload) await this.reloadMenu();
        return {
            err: res.executionStatus !== ExecutionStatus.Finished
        };
    }

    async updateExternalId(menuItemId: number, externalId: string) {
        await updateMenuItemExternalId({
            menuId: this.currentMenu.id,
            menuItemId,
            externalId
        })
    }

    async removeMenuItem(menuId: number, menuItemId: number) {
        const res = await deleteMenuItem({
            menuItemId, menuId
        });
        await this.reloadMenu();
        return {
            err: res.executionStatus !== ExecutionStatus.Finished
        };
    }



    @action setCategoryName(name:LocalizedString) {
        this.forms.forms[MenuFormType.createMenuCategory].current.data.name = name;
    }

    @action setMenuItemName(name:LocalizedString) {
        this.currentMenuItem.data.name = name;
        this.updateMenuItem(this.currentMenuItem.id, {
            ...this.currentMenuItem.data,
            name: this.currentMenuItem.data.name
        }, false);
    }
    @action setMenuItemDescription(description:LocalizedString) {
        this.currentMenuItem.data.description = description;
        this.updateMenuItem(this.currentMenuItem.id, {
            ...this.currentMenuItem.data,
            description: this.currentMenuItem.data.description
        }, false);
    }

    @action
    setCurrentMenuId(menuId: number) {
        this.currentMenu.id = menuId;
    }

    @action
    setSelectedCategory(category: number) {
        this.selectedCategory = category;
    }


    @computed
    getCategoryById(id: number) {
        return this.currentMenu.menuCategories.find(item => item.id === id);
    }


    @computed
    getMenuItemByCategoryIdAndId(categoryId: number, itemId: number): MenuItem {
        return this.currentMenu.menuItemsByCategoryId[categoryId].find(item => item.id === itemId);
    }

    @action
    reset() {
        this.currentMenu = undefined;
        this.forms = new MenuFormsStore();
        this.selectedCategory = 0;
    }

    @action
    resetNewCategory() {
        this.newCategoryId = undefined;
    }


    @computed
    hasIIkoIntegration() {
        const settings = this.currentMenu?.menu.data.externalIntegrationSettings;

        if (settings === null || !settings) {
            return false
        } else {
            return settings.descriptor.type === ExternalIntegrationType.iikoCloud && settings.isEnabled
        }
    }

    @action
    resetCategorySchedule() {
        this.categoryWorkingSchedule = defaultMenuCategoryWorkingSchedule;
    }
    @action
    resetItemSchedule() {
        this.itemWorkingSchedule = defaultMenuItemWorkingSchedule;
    }
    @action
    resetCategoryCookingTime() {
        this.categoryCookingTime = {fromTime: null, toTime: null};
    }

    @action
    resetItemCookingTime() {
        this.itemCookingTime = {fromTime: null, toTime: null};
    }

    @action
    setMondayItemSchedule(workingSchedule: MenuItemWorkingScheduleDay) {
        this.itemWorkingSchedule = {
            ...this.itemWorkingSchedule,
            monday: workingSchedule
        }
    }

    @action
    setTuesdayItemSchedule(workingSchedule: MenuItemWorkingScheduleDay) {
        this.itemWorkingSchedule = {
            ...this.itemWorkingSchedule,
            tuesday: workingSchedule

        }
    }

    @action
    setWednesdayItemSchedule(workingSchedule: MenuItemWorkingScheduleDay) {
        this.itemWorkingSchedule = {
            ...this.itemWorkingSchedule,
            wednesday: workingSchedule

        }
    }

    @action
    setThursdayItemSchedule(workingSchedule: MenuItemWorkingScheduleDay) {
        this.itemWorkingSchedule = {
            ...this.itemWorkingSchedule,
            thursday: workingSchedule

        }
    }

    @action
    setFridayItemSchedule(workingSchedule: MenuItemWorkingScheduleDay) {
        this.itemWorkingSchedule = {
            ...this.itemWorkingSchedule,
            friday: workingSchedule

        }
    }

    @action
    setSaturdayItemSchedule(workingSchedule: MenuItemWorkingScheduleDay) {
        this.itemWorkingSchedule = {
            ...this.itemWorkingSchedule,
            saturday: workingSchedule

        }
    }

    @action
    setSundayItemSchedule(workingSchedule: MenuItemWorkingScheduleDay) {
        this.itemWorkingSchedule = {
            ...this.itemWorkingSchedule,
            sunday: workingSchedule
        }
    }

    @action
    setMondayCategorySchedule(workingSchedule: MenuCategoryWorkingScheduleDay) {
        this.categoryWorkingSchedule = {
            ...this.categoryWorkingSchedule,
            monday: workingSchedule
        }
    }

    @action
    setTuesdayCategorySchedule(workingSchedule: MenuCategoryWorkingScheduleDay) {
        this.categoryWorkingSchedule = {
            ...this.categoryWorkingSchedule,
            tuesday: workingSchedule

        }
    }

    @action
    setWednesdayCategorySchedule(workingSchedule: MenuCategoryWorkingScheduleDay) {
        this.categoryWorkingSchedule = {
            ...this.categoryWorkingSchedule,
            wednesday: workingSchedule

        }
    }

    @action
    setThursdayCategorySchedule(workingSchedule: MenuCategoryWorkingScheduleDay) {
        this.categoryWorkingSchedule = {
            ...this.categoryWorkingSchedule,
            thursday: workingSchedule

        }
    }

    @action
    setFridayCategorySchedule(workingSchedule: MenuCategoryWorkingScheduleDay) {
        this.categoryWorkingSchedule = {
            ...this.categoryWorkingSchedule,
            friday: workingSchedule

        }
    }

    @action
    setSaturdayCategorySchedule(workingSchedule: MenuCategoryWorkingScheduleDay) {
        this.categoryWorkingSchedule = {
            ...this.categoryWorkingSchedule,
            saturday: workingSchedule

        }
    }

    @action
    setSundayCategorySchedule(workingSchedule: MenuCategoryWorkingScheduleDay) {
        this.categoryWorkingSchedule = {
            ...this.categoryWorkingSchedule,
            sunday: workingSchedule

        }
    }
    @action
    setCategoryCookingTime (cookingTime: CookingTime) {
        this.categoryCookingTime = cookingTime;
    }
    @action
    setItemCookingTime (cookingTime: CookingTime) {
        this.itemCookingTime = cookingTime;
    }


    async toggleStopList(item: MenuItem, value: boolean){
        const res = await this.updateMenuItem(item.id, {
            ...item.data,
            isInStopList: value
        });
        return res.err
    }

}

export default MenuStore;
